import { ApiClient } from '@squire/api-client';
import type { TokenPair } from '@squire/api-client';
import type { AxiosInstance } from 'axios';
import { Platform } from 'react-native';

import { toast } from '@/components/_shared/Toaster';
import { storage, storageKeys } from '@/constants/storage';
import { getClientHeaders, getClientIdentity } from '@/utils/clientIdentity';
import { forceLogout } from '@/utils/sessionUtils';

import { getEnvironmentConfig } from './config';
import i18n from './i18n';

const API_BASE_URL = __DEV__
  ? process.env.EXPO_PUBLIC_API_BASE_URL || getEnvironmentConfig().apiBaseUrl
  : getEnvironmentConfig().apiBaseUrl;

// The api-client tears a dead session down through two paths: a TOKEN_EXPIRED refresh
// calls `clearTokens` and then `onSessionExpired`; any other non-transient 4xx from the
// refresh endpoint calls `clearTokens` only. Both must leave the app signed out (tokens
// AND customerId gone) with a single toast, so they share this guarded routine. SQR-15538.
const expireSession = (): void => {
  if (!storage.getString(storageKeys.authToken)) return;

  forceLogout();
  toast({
    variant: 'error',
    message: i18n.t('app.sessionExpired.message'),
  });
};

const clientIdentity = getClientIdentity();

const apiClient = new ApiClient(API_BASE_URL, {
  // --- v1 callbacks (kept for backward compat) ---
  clearTokens: async () => {
    expireSession();
  },
  getTokens: async () => {
    const token = storage.getString(storageKeys.authToken) || '';
    return { token };
  },
  setTokens: async (data) => {
    storage.set(storageKeys.authToken, data.token);
  },
  refreshTokenV2Endpoint: '/v2/refresh-token',
  refreshTokenEndpoint: '/v1/refresh-token',
  authPrefix: 'JWT',

  // --- v2 callbacks ---
  getTokenPair: async (): Promise<TokenPair | null> => {
    const accessToken = storage.getString(storageKeys.authToken);
    if (!accessToken) return null;

    return {
      accessToken,
      accessTokenExpiresAt:
        storage.getString(storageKeys.accessTokenExpiresAt) || undefined,
      refreshToken: storage.getString(storageKeys.refreshToken) || undefined,
      refreshTokenExpiresAt:
        storage.getString(storageKeys.refreshTokenExpiresAt) || undefined,
    };
  },
  setTokenPair: async (pair: TokenPair): Promise<void> => {
    storage.set(storageKeys.authToken, pair.accessToken);
    if (pair.refreshToken) {
      storage.set(storageKeys.refreshToken, pair.refreshToken);
    }
    if (pair.accessTokenExpiresAt) {
      storage.set(storageKeys.accessTokenExpiresAt, pair.accessTokenExpiresAt);
    }
    if (pair.refreshTokenExpiresAt) {
      storage.set(
        storageKeys.refreshTokenExpiresAt,
        pair.refreshTokenExpiresAt,
      );
    }
  },
  onSessionExpired: expireSession,

  headers: {
    // Browsers forbid overriding User-Agent, so native only.
    ...(Platform.OS !== 'web' && {
      'User-Agent': `Flagship - ${clientIdentity.platform} ${clientIdentity.version}`,
    }),
    ...getClientHeaders(clientIdentity),
  },
});

// Add request interceptor to dynamically set Preferred-Language header
// This ensures language changes take effect immediately without app restart
const axiosInstance = (apiClient as unknown as { axiosInstance: AxiosInstance })
  .axiosInstance;

axiosInstance.interceptors.request.use((config) => {
  config.headers['Preferred-Language'] = i18n.language;
  return config;
});

export default apiClient;
